import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useHasPermission } from '@/hooks/permissions/useHasPermission';
import { toast } from 'sonner';

/**
 * Shift swap & offer configuration toggles.
 *
 * Self-gates on `system_config_manage` — renders nothing if the current user
 * lacks that permission.  This allows the component to be placed on pages
 * whose route guard uses a *different* permission (e.g. `can_manage_roles`
 * on Access & Permissions) without exposing controls to unauthorized users.
 *
 * Data layer: reads/writes via `useCompanySettings` hook which calls
 * `get_company_settings` / `set_company_settings` RPCs (both independently
 * gated by `system_config_manage` at the DB level).
 */
export function ShiftSwapSettings() {
  const { t } = useTranslation();
  const canManageSystemConfig = useHasPermission('system_config_manage');
  const { settings: companySettings, isLoading, updateSettings } = useCompanySettings();

  if (!canManageSystemConfig) return null;

  const handleChange = async (key: 'enable_offers' | 'enable_swaps' | 'require_approval_offers' | 'require_approval_swaps', value: boolean) => {
    try {
      await updateSettings({ [key]: value });
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast.error('Failed to update settings');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('settings.companySettings', 'Company Settings')}
        </CardTitle>
        <CardDescription>
          {t('settings.companySettingsDescription', 'Configure scheduling features for your company')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Feature toggles */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">
                {t('settings.enableOffers', 'Enable Offer Shifts')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t('settings.enableOffersDescription', 'Allow employees to offer their shifts to open shifts')}
              </div>
            </div>
            <Switch
              checked={companySettings.enable_offers}
              disabled={isLoading}
              onCheckedChange={(checked) => handleChange('enable_offers', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">
                {t('settings.enableSwaps', 'Enable Swap Shifts')}
              </div>
              <div className="text-sm text-muted-foreground">
                {t('settings.enableSwapsDescription', 'Allow employees to request shift swaps with each other')}
              </div>
            </div>
            <Switch
              checked={companySettings.enable_swaps}
              disabled={isLoading}
              onCheckedChange={(checked) => handleChange('enable_swaps', checked)}
            />
          </div>
        </div>

        {/* Approval settings */}
        <div className="border-t pt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">
                  {t('settings.requireApprovalOffers', 'Require approval for offered shifts')}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('settings.requireApprovalOffersDescription', 'Offered shifts need manager approval before becoming available')}
                </div>
              </div>
              <Switch
                checked={companySettings.require_approval_offers}
                disabled={isLoading}
                onCheckedChange={(checked) => handleChange('require_approval_offers', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">
                  {t('settings.requireApprovalSwaps', 'Require approval for swap requests')}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('settings.requireApprovalSwapsDescription', 'Swap requests need manager approval before being processed')}
                </div>
              </div>
              <Switch
                checked={companySettings.require_approval_swaps}
                disabled={isLoading}
                onCheckedChange={(checked) => handleChange('require_approval_swaps', checked)}
              />
            </div>
          </div>

          {/* Future approver roles section */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-sm text-muted-foreground">
              <strong>Who can approve:</strong> {companySettings.approver_roles.join(', ')}
              <br />
              <em>These settings take effect when approvals are enabled in a future update.</em>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}